import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, logout, user, role, loading } = useAuth();

  const [selectedRole, setSelectedRole] = useState<'yonetici' | 'ogretmen'>('yonetici');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const roleMismatchRef = useRef(false);

  useEffect(() => {
    if (!loading && user && role && !roleMismatchRef.current) {
      if (role === 'yonetici') navigate('/admin/dashboard', { replace: true });
      else navigate('/teacher/dashboard', { replace: true });
    }
  }, [user, role, loading, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Lütfen kullanıcı adı ve şifrenizi girin.');
      return;
    }

    setSubmitting(true);
    const { error: loginError, role: loggedRole } = await login(email, password);
    setSubmitting(false);

    if (loginError) {
      setError(loginError);
      return;
    }

    if (loggedRole && loggedRole !== selectedRole) {
      const gercek = loggedRole === 'yonetici' ? 'Yönetici' : 'Öğretmen';
      roleMismatchRef.current = true;
      await logout();
      roleMismatchRef.current = false;
      setError(`Bu hesap bir ${gercek} hesabıdır. Lütfen "${gercek}" seçeneğini seçerek giriş yapın.`);
      return;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0d1b3e]">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-4xl text-white/60 animate-spin" />
          <p className="text-white/50 text-sm">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#0d1b3e] relative overflow-hidden">
      {/* Dot decorations */}
      <div className="absolute top-12 right-[42%] w-1.5 h-1.5 rounded-full bg-white/20 pointer-events-none" />
      <div className="absolute top-32 right-[38%] w-1 h-1 rounded-full bg-white/15 pointer-events-none" />
      <div className="absolute bottom-20 right-[45%] w-1 h-1 rounded-full bg-white/20 pointer-events-none" />
      <div className="absolute top-1/3 left-[35%] w-1.5 h-1.5 rounded-full bg-white/10 pointer-events-none" />

      {/* LEFT PANEL */}
      <div className="hidden lg:flex flex-col justify-between flex-1 px-16 py-14">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 border border-white/20">
            <i className="ri-book-open-line text-white text-xl" />
          </div>
          <span className="text-white font-bold text-xl tracking-wider">DERSEGEL</span>
        </div>

        {/* Hero text */}
        <div className="mt-16">
          <h1 className="text-4xl font-bold text-white leading-tight mb-5">
            Eğitim Operasyonunu<br />
            <span className="text-[#6b9fe4]">Tek Merkezden</span><br />
            Yönet
          </h1>
          <p className="text-white/55 text-base leading-relaxed max-w-sm">
            Başvurudan ödemeye, deneme dersinden aktif sürece kadar tüm süreçleri akıllıca yönet.
          </p>
        </div>

        {/* Features */}
        <div className="space-y-4 mt-auto pt-16">
          {[
            { icon: 'ri-user-star-line', text: 'Öğretmen & Öğrenci Yönetimi' },
            { icon: 'ri-coin-line', text: 'Ödeme & Hak Ediş Takibi' },
            { icon: 'ri-bar-chart-2-line', text: 'Gelir & Performans Raporları' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 border border-white/10">
                <i className={`${item.icon} text-[#6b9fe4] text-base`} />
              </div>
              <span className="text-white/65 text-sm">{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT PANEL — Login Card */}
      <div className="flex items-center justify-center w-full lg:w-auto lg:min-w-[520px] lg:max-w-[520px] px-6 py-10 lg:py-0">
        <div className="w-full max-w-md bg-white rounded-2xl p-8 md:p-10">
          {/* Card logo */}
          <div className="flex flex-col items-center mb-7">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#0d1b3e]">
                <i className="ri-book-open-line text-white text-lg" />
              </div>
              <span className="text-[#0d1b3e] font-bold text-2xl tracking-wider">DERSEGEL</span>
            </div>
            <p className="text-gray-400 text-sm">Antalya · Online Özel Ders Operasyon Paneli</p>
          </div>

          {/* Role selector */}
          <div className="flex rounded-xl border border-gray-200 overflow-hidden mb-7 p-1 gap-1 bg-gray-50">
            <button
              type="button"
              onClick={() => setSelectedRole('yonetici')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${
                selectedRole === 'yonetici'
                  ? 'bg-[#0d1b3e] text-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-shield-user-line text-base" />
              Yönetici
            </button>
            <button
              type="button"
              onClick={() => setSelectedRole('ogretmen')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${
                selectedRole === 'ogretmen'
                  ? 'bg-[#0d1b3e] text-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-user-line text-base" />
              Öğretmen
            </button>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            {/* Username / Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                E-posta Adresi
              </label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                  <i className="ri-user-3-line text-base" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-posta adresinizi girin"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0d1b3e]/15 focus:border-[#0d1b3e] transition-all bg-white"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Şifre
              </label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                  <i className="ri-lock-2-line text-base" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Şifrenizi girin"
                  className="w-full pl-10 pr-12 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0d1b3e]/15 focus:border-[#0d1b3e] transition-all bg-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <i className="ri-error-warning-line text-red-500 text-base" />
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className={`w-full py-3.5 rounded-xl font-semibold text-sm text-white transition-all whitespace-nowrap cursor-pointer bg-[#0d1b3e] hover:bg-[#162852] ${submitting ? 'opacity-70' : ''}`}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  Giriş yapılıyor...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-login-box-line" />
                  Giriş Yap
                </span>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-5">
            © 2026 Dersegel Antalya · Tüm hakları saklıdır
          </p>
        </div>
      </div>
    </div>
  );
}